import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { catchError, of } from 'rxjs';
import { take, timeout } from 'rxjs/operators';
import { Vehicle } from '../models/core.models';
import { VehiclesFirebaseService } from '../services/firebase';

/**
 * Resolves the active marketplace inventory for /search/:region so the region
 * landing page can render representative motorhome cards in its prerendered
 * HTML (HMM-162 IL-1). The Router awaits resolvers before the route renders,
 * which makes the read deterministic under prerender — subscribing to the
 * live read inside the component instead raced the SSR stability snapshot and
 * left most region pages with zero crawlable /motorhome/ links.
 *
 * Reads via `getActiveVehiclesOnce()` — a REST `getDocs` one-shot query
 * (no WebChannel listener) wrapped in `trackPending`, so SSR awaits it before
 * snapshotting the HTML.
 */
export const regionVehiclesResolver: ResolveFn<Vehicle[]> = () => {
  const vehiclesService = inject(VehiclesFirebaseService);

  return vehiclesService.getActiveVehiclesOnce().pipe(
    take(1), // getDocs emits once then completes; complete the resolve.
    timeout(5000), // Don't hang the render if Firestore is slow/unreachable.
    catchError(() => of([] as Vehicle[])),
  );
};
